/**
 * ⑦ 프로젝트 종료 창의 판정 — 순수 함수(화면과 떼어 검사한다).
 *
 * 왜 있나: prod #35 는 22문제를 검수·분석한 뒤 ⑥ 등록 없이 ⑦ 을 눌러 시험지 없이 닫혔다. 종전 창은
 * 등록 0건이어도 「시험지로 저장하고 종료」라고 말했고, 서버 409 를 받은 뒤에야 개발자 말로 되물었다.
 * 이제 창을 여는 순간 **무엇이 남았는지 세어** 버튼을 고른다(BE-20260925 F12, 오너 결정 2026-09-25
 * — 등록할 것이 남았으면 주 버튼은 「N건 등록 창 열기」).
 *
 * 세는 규칙은 서버 종료 가드(api/v2/ocr.py close — prod 35 대응, dev a70b53a)와 같다:
 *   대상 = 미등록 · 반려(400) 아님 · (검수 200 이거나 분석 200)
 *     검수 ≠ 200            → rereview        (⑤ 결과 승인 필요)
 *     분석 ≠ 200            → analysisPending (⑤ AI 분석 필요)
 *     차단 플래그·정답·본문 없음 → blocked        (④ 확인 필요 — 서버 check_promotable)
 *     그 밖                  → ready           (⑥ 에서 바로 등록됨)
 * 이미 등록됐지만 OCR 쪽이 바뀐 행(needs_repromote)은 repromote 로 따로 센다 — 다시 등록해야 은행·시험지에 반영된다.
 */
import { getBlockingFlags } from './ocrApi';

export function closeStageCounts(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const c = { promoted: 0, repromote: 0, ready: 0, rereview: 0, analysisPending: 0, blocked: 0 };
  list.forEach((p) => {
    if (p?.promoted === true) {
      c.promoted += 1;
      if (p.needs_repromote === true) c.repromote += 1;
      return;
    }
    const rv = p?.review_status;
    const an = p?.analysis_status;
    if (rv === 400) return;
    if (rv !== 200 && an !== 200) return; // 손대지 않은 행 — 서버도 세지 않는다
    if (rv !== 200) { c.rereview += 1; return; }
    if (an !== 200) { c.analysisPending += 1; return; }
    const blocked = getBlockingFlags(p.flags).length > 0 || p.has_answer === false || p.has_body === false;
    if (blocked) c.blocked += 1;
    else c.ready += 1;
  });
  c.reviewed = c.ready + c.rereview + c.analysisPending + c.blocked;
  return c;
}

/**
 * 창의 모양을 고른다.
 *   counts        — closeStageCounts 결과. null 이면 세지 못한 것(조회 실패) — 서버가 다시 확인한다.
 *   promotedCount — 서버 집계(stats.promoted). counts 가 있으면 counts.promoted 를 쓴다.
 *
 * 반환 mode:
 *   'register' — 등록할 것이 남음(ready 또는 repromote). 주 버튼 「N건 등록 창 열기」, 약한 버튼 「등록하지 않고 종료」
 *   'pending'  — 등록 0건 · 등록 가능 0 · 그래도 검수·분석한 것이 있음. 할 일만 적고 「등록하지 않고 종료」만
 *   'paper'    — 등록 1건 이상, 더 등록할 것 없음. 주 버튼 「시험지로 저장하고 종료」
 *   'empty'    — 등록 0건 · 검수·분석한 것도 없음. 주 버튼 「시험지 없이 종료」
 *   'unknown'  — 세지 못함. 주 버튼 「종료」 — 서버 409 가 무엇이 남았는지 알려 준다
 * willMakePaper — 이 종료가 시험지를 만드는가(등록 1건 이상). 제목 칸은 이때만 뜻이 있다.
 * discardCount  — 「등록하지 않고 종료」가 문제은행에 넣지 않고 두는 수
 */
export function closeMode(counts, promotedCount = null) {
  if (!counts) {
    const p = Number(promotedCount);
    return { mode: 'unknown', willMakePaper: Number.isFinite(p) && p > 0, discardCount: null, registerCount: 0 };
  }
  const registerCount = counts.ready + counts.repromote;
  const willMakePaper = counts.promoted > 0;
  if (registerCount > 0) {
    return { mode: 'register', willMakePaper, discardCount: counts.reviewed, registerCount };
  }
  if (!willMakePaper) {
    return counts.reviewed > 0
      ? { mode: 'pending', willMakePaper, discardCount: counts.reviewed, registerCount: 0 }
      : { mode: 'empty', willMakePaper, discardCount: 0, registerCount: 0 };
  }
  return { mode: 'paper', willMakePaper, discardCount: counts.reviewed, registerCount: 0 };
}

/**
 * 종료 409 를 갈래로 나눈다. 서버의 409 셋 중 사유 코드가 붙는 것은 unpromoted_reviewed 하나뿐이고,
 * 등록 0건과 시험지 문항 검증 실패는 사유 없이 문구만 온다(api/v2/ocr.py close).
 *   'unpromoted' — data 에 단계별 수가 온다 → counts 꼴로 옮겨 돌려준다
 *   'invalid'    — 시험지 문항 검증 실패. 시험지 없이 닫는 길을 권하지 않는다(등록분이 있어 force 로도 같은 실패)
 *   'no_promoted'— 등록 0건(옛 서버의 「승격 0건」 포함). 시험지 없이 닫을지 물을 수 있다
 */
export function classifyClose409(err) {
  const data = err?.data;
  if (data && data.reason === 'unpromoted_reviewed') {
    const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
    const counts = {
      promoted: 0,
      repromote: 0,
      ready: n(data.ready),
      rereview: n(data.rereview),
      analysisPending: n(data.analysis_pending),
      blocked: n(data.blocked),
    };
    counts.reviewed = n(data.reviewed) || (counts.ready + counts.rereview + counts.analysisPending + counts.blocked);
    return { kind: 'unpromoted', counts };
  }
  if (String(err?.message || '').startsWith('시험지 문항 검증 실패')) return { kind: 'invalid' };
  return { kind: 'no_promoted' };
}
